import React from 'react';
import {
	Box,
	Button,
	IconButton,
	ListItem,
	ListItemText,
	Switch,
	Theme,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { createStyles } from '@mui/styles';
import { RiPencilLine, RiDeleteBinLine } from 'react-icons/ri';
import { ManualPaymentMethodDetails } from 'common/modules/payments/types';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import { getTranslation } from 'common/modules/translations';
import { shopHasManualPaymentMethod } from 'common/modules/payments/paymentMethods';
import { useTranslation } from 'services/localization';

interface Props {
	paymentMethodObject: ManualPaymentMethodDetails;
	onDelete: (id: string) => void;
	onToggle: (id: string, enabled: boolean) => void;
	onEdit: (id?: string, edit?: boolean) => void;
}
const ManualPaymentMethodRow = (props: Props) => {
	const classes = useStyles();
	const theme = useTheme();
	const { t } = useTranslation();
	const { paymentMethodObject, onDelete, onToggle, onEdit } = props;
	const lang = useSelector(LanguageSelectors.language);
	const selectedPaymentMethods = useSelector(ShopSelectors.getActivePaymentMethods);
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const isEnabled = shopHasManualPaymentMethod(paymentMethodObject.id, selectedPaymentMethods);

	return (
		<ListItem divider className={classes.listItem}>
			<ListItemText
				classes={{
					root: classes.listItemText,
					inset: classes.listItemTextInset,
				}}
				inset
				primary={
					<Box sx={{ fontWeight: 400 }}>
						<Switch
							checked={isEnabled}
							onClick={() => onToggle(paymentMethodObject.id, !isEnabled)}
						/>
						{!!paymentMethodObject ? getTranslation(paymentMethodObject.label, lang) : ''}
					</Box>
				}
				secondary={
					!!paymentMethodObject.description
						? getTranslation(paymentMethodObject.description, lang)
						: ''
				}
				secondaryTypographyProps={{
					sx: {
						marginLeft: theme.spacing(7),
					},
				}}
			/>
			<ListItemText
				classes={{
					root: classes.listItemButtons,
					inset: classes.listItemTextInset,
				}}
				inset
				primary={
					<IconButton
						onClick={() => {
							onEdit(paymentMethodObject.id, true);
						}}
					>
						{!isMobile ? (
							<RiPencilLine color={theme.palette.colors.tundra.main} size={24} />
						) : (
							<Button
								color="info"
								sx={{ marginLeft: theme.spacing(5.5), justifyContent: 'flex-start' }}
								variant="text"
							>
								{t('common:actions.edit')}
							</Button>
						)}
					</IconButton>
				}
				secondary={
					<IconButton onClick={() => onDelete(paymentMethodObject.id)}>
						{!isMobile ? (
							<RiDeleteBinLine color={theme.palette.colors.tundra.main} size={24} />
						) : (
							<Button sx={{ justifyContent: 'flex-start' }} variant="text" color="error">
								{t('common:actions.delete')}
							</Button>
						)}
					</IconButton>
				}
				secondaryTypographyProps={{
					className: classes.listItemButtons,
				}}
			/>
		</ListItem>
	);
};
const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		listItem: {
			padding: theme.spacing(2, 2, 2, 0),
			[theme.breakpoints.down('sm')]: {
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
			},
		},
		listItemText: {
			minWidth: '20%',
			[theme.breakpoints.down('sm')]: {
				paddingRight: 0,
			},
		},
		listItemTextInset: {
			paddingLeft: 0,
		},
		listItemButtons: {
			paddingRight: 0,
			justifyContent: 'flex-end',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
		},
	}),
);

export default ManualPaymentMethodRow;
